import { createAsyncThunk } from "@reduxjs/toolkit";
import { connectionHubApi } from "../api/connectionHub.api";
import type {
  AccountRow,
  ChannelCatalogResponse,
  ConnectionHubError,
  EmailSuggestions,
} from "../api/connectionHub.types";
import { handleConnectionError, isConnectionHubError } from "../utils/handleConnectionError";

interface AgencyReadArgs {
  agencyClientId?: number | null;
}

interface AccountReadArgs extends AgencyReadArgs {
  ipId: string;
}

const normalizeAgencyClientId = (value?: number | null): number | null =>
  typeof value === "number" ? value : null;

export const fetchConnectionChannels = createAsyncThunk<
  { data: ChannelCatalogResponse; agencyClientId: number | null },
  AgencyReadArgs | undefined,
  { rejectValue: ConnectionHubError }
>("connectionHub/fetchChannels", async (args, { rejectWithValue }) => {
  try {
    const agencyClientId = normalizeAgencyClientId(args?.agencyClientId);
    return {
      data: await connectionHubApi.getChannels(agencyClientId),
      agencyClientId,
    };
  } catch (error) {
    return rejectWithValue(isConnectionHubError(error) ? error : handleConnectionError(error));
  }
});

export const fetchConnectionAccounts = createAsyncThunk<
  { data: AccountRow[]; ipId: string; agencyClientId: number | null },
  AccountReadArgs,
  { rejectValue: ConnectionHubError }
>("connectionHub/fetchAccounts", async (args, { rejectWithValue }) => {
  try {
    const agencyClientId = normalizeAgencyClientId(args.agencyClientId);
    return {
      data: await connectionHubApi.getAccounts(args.ipId, agencyClientId),
      ipId: args.ipId,
      agencyClientId,
    };
  } catch (error) {
    return rejectWithValue(isConnectionHubError(error) ? error : handleConnectionError(error));
  }
});

export const fetchConnectionEmailSuggestions = createAsyncThunk<
  { data: EmailSuggestions; agencyClientId: number | null },
  AgencyReadArgs | undefined,
  { rejectValue: ConnectionHubError }
>("connectionHub/fetchEmailSuggestions", async (args, { rejectWithValue }) => {
  try {
    const agencyClientId = normalizeAgencyClientId(args?.agencyClientId);
    return {
      data: await connectionHubApi.getEmailSuggestions(agencyClientId),
      agencyClientId,
    };
  } catch (error) {
    return rejectWithValue(isConnectionHubError(error) ? error : handleConnectionError(error));
  }
});
